// What a widget value gives the form renderer (spec §14, Phase 12): the form the widget shows and the meta that says which
// widget it was. Pure, so it runs under `node --test`.

const is_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// The form of a widget of the form slot (data type 6): `form_definition_data`, what the public `getFormDefinition` answers.
// It is null when the form was trashed, has no version, or the widget has no form - then the widget draws nothing.
export const widget_form_of = (widget) => {
    const form = widget?.form_definition_data;
    return is_object(form) && is_object(form.version) ? form : null;
};

// The id of the widget travels with the answer as `meta.extra.widget_id`, so the admin sees where it was sent from. A
// `widget_id` the site gave itself is kept; a widget without an id adds nothing. Returns undefined for no meta at all.
export const widget_extra_meta = (widget, extra_meta) => {
    const given = is_object(extra_meta) ? extra_meta : undefined;
    const id = widget?.widget_id ?? widget?.id;
    if (id === undefined || id === null || id === "" || (given && Object.prototype.hasOwnProperty.call(given, "widget_id"))) {
        return given;
    }
    return { ...(given || {}), widget_id: String(id) };
};

// The page query has no form for a widget of the form slot, so a server page adds it before drawing (added for jument: the contact
// and employment pages). `sections` is the page's widgets ([{ section, widgets }]); every widget whose `widget_component_key` is in
// `component_keys` gets `form_definition_id` and `form_definition_data` from `load_form(widget_id)` (null when it has none, or when
// loading failed); the rest are kept as they are. Loads run side by side; nothing is changed in place. Server side:
// with_widget_forms_server (helpers/server/widget-forms.js) passes the uncached getWidgetValueDetails query as load_form.
export const attach_widget_forms = async (sections, component_keys, load_form) => {
    const keys = new Set(Array.isArray(component_keys) ? component_keys : []);
    if (!Array.isArray(sections) || keys.size === 0 || typeof load_form !== "function") {
        return sections;
    }
    return Promise.all(sections.map(async (section) => {
        if (!Array.isArray(section?.widgets)) {
            return section;
        }
        const widgets = await Promise.all(section.widgets.map(async (widget) => {
            if (!keys.has(widget?.widget_component_key) || !widget?.widget_id) {
                return widget;
            }
            let loaded = null;
            try {
                loaded = await load_form(widget.widget_id);
            } catch {
                loaded = null;
            }
            return {
                ...widget,
                form_definition_id: loaded?.form_definition_id ?? null,
                form_definition_data: is_object(loaded?.form_definition_data) ? loaded.form_definition_data : null,
            };
        }));
        return { ...section, widgets };
    }));
};
